'use client';
import type {HTMLChakraProps} from '@chakra-ui/react';
import {chakra, css, getToken, keyframes, useTheme} from '@chakra-ui/react';
import type {ReactNode} from 'react';
import {useMemo, useState} from 'react';
import type {TooltipProps} from 'recharts';
import {Cell, Pie, PieChart as RechartsPieChart, ResponsiveContainer, Text, Tooltip} from 'recharts';

import {tokens} from '../../generated/light';
import {mergeSXProp} from '../../utils/merge-sx-prop';

import {parts} from './theme/anatomy';
import {useChartPosition} from './useChartPosition';

export type ChartItem<T = string> = {name: T; value: number; color?: string; shadow?: string};

type Props<T extends string> = {
  items: Array<ChartItem<T>>;
  isLoaded?: boolean;
  tooltip?: (props: TooltipProps<any, T>, chartPosition: DOMRect | null) => ReactNode;
  showTotal?: boolean;
} & HTMLChakraProps<`div`>;

export const PieChart = <T extends string>(props: Props<T>) => {
  const {items, isLoaded, tooltip, showTotal = true, sx = {}, ...rest} = props;
  const theme = useTheme();
  const {ref, rect: chartPosition} = useChartPosition();
  const getColor = (color?: string) =>
    getToken(`colors`, color?.includes(`.`) ? color : `${color}.600`, `colors.obsidian.200`)(theme);

  const [activeIndex, setActiveIndex] = useState(-1);
  const onPieEnter = (_: MouseEvent, index: number) => {
    setActiveIndex(index);
  };
  const onPieLeave = () => {
    setActiveIndex(-1);
  };

  const total = useMemo(() => items.reduce((acc, item) => acc + item.value, 0), [items]);

  return (
    <>
      <Container
        ref={ref}
        width={rest.width ?? `100%`}
        height={rest.height ?? `100%`}
        position={`relative`}
        display={`flex`}
        {...rest}
        {...parts.container.attrs}
        {...(isLoaded ? {} : cssAnimation(theme))}
        minW={`200px`}
        sx={mergeSXProp(sx, {
          [`.recharts-surface`]: {overflow: `visible`},
          [`.total-label`]: {
            fontSize: tokens.core.fontSizes.text.lg,
            fontWeight: tokens.core.fontWeights.text.lg,
            lineHeight: tokens.core.lineHeights.text.lg,
          },
        })}
      >
        {showTotal && isLoaded && (
          <Text
            style={{
              position: `absolute`,
              top: `50%`,
              left: `50%`,
              transform: `translate(-50%, -50%)`,
              // Recharts has logic tp calculate the text width by adding a node to the dom and measuring it
              // Sometimes the measurement node is not removed and it results in 'phantom' element.
              // Since the cacheKey is an stringify of the 'style' object, we use it to force the removal of the element
              //  https://github.com/recharts/recharts/blob/3.x/src/util/DOMUtils.ts#L86
              content: `${total}-${isLoaded}`,
            }}
            textAnchor={`middle`}
            {...parts.totalLabel.attrs}
            className={`total-label`}
          >
            {new Intl.NumberFormat().format(total)}
          </Text>
        )}
        <RechartsPieChart>
          {isLoaded ? (
            <>
              <Pie
                data={items.sort((a, b) => b.value - a.value)}
                innerRadius={`85%`}
                outerRadius={`100%`}
                dataKey={`value`}
                cornerRadius={10}
                paddingAngle={-10}
                minAngle={20}
                startAngle={-360}
                endAngle={-720}
                onMouseEnter={onPieEnter}
                onMouseLeave={onPieLeave}
              >
                {items.map((entry, index) => (
                  <Cell
                    key={`cell-${entry.name}`}
                    fill={getColor(entry.color)}
                    // When there is one item do show the stroke (leaves a small gap in the Doughnut)
                    stroke={items.length === 0 ? undefined : `0`}
                    style={{
                      zIndex: index === activeIndex ? index : 0,
                      outline: `none`,
                      filter: index === activeIndex ? `drop-shadow(0px 4px 28px ${getColor(entry.shadow)})` : `none`,
                    }}
                  />
                ))}
              </Pie>
            </>
          ) : (
            <Pie
              dataKey={`value`}
              data={[{value: 130}, {value: 30}]}
              innerRadius={`85%`}
              outerRadius={`94%`}
              animationDuration={10 * 1000}
            >
              <Cell
                key={`cell-loading`}
                stroke={`0`}
                opacity={0.5}
                fill={getColor(`obsidian.300`)}
                style={{
                  outline: `none`,
                }}
              />
              <Cell
                key={`cell-loading2`}
                stroke={`0`}
                opacity={0.5}
                fill={getColor(`obsidian.400`)}
                data-id={`loading-track`}
              />
            </Pie>
          )}
          {tooltip ? (
            <Tooltip content={(tooltipProps: TooltipProps<any, T>) => tooltip(tooltipProps, chartPosition)} />
          ) : null}
        </RechartsPieChart>
      </Container>
    </>
  );
};

export type {Props as PieChartProps};

const Container = chakra(ResponsiveContainer, {});

const spinAnimation = keyframes(`
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`);

const cssAnimation = css(() => ({
  animation: `${spinAnimation} 1s linear infinite`,
}));
